import {useMemo} from 'react';
import {DisplayAdContainer} from '../../../../common/containers/DisplayAdContainer/DisplayAdContainer';
import {PromotionBannerContainer} from '../../../../common/containers/PromotionBannerContainer';
import {t} from '../../../../common/getTranslations';
import {LogTabType} from '../../../../common/LogTabType.enum';
import {TabPositionSticky} from '../../../../common/presentational/TabPositionSticky';
import {formatWithCommas} from '../../../../common/shared/utils/formatWithCommas';
import {twMerge} from '../../../../common/twMerge';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {PageViewCustomPropsDataKey} from '../../../../logging/PageViewCustomPropsDataKey.enum';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {IconType} from '../../../atoms/Icon/IconType.enum';
import {CheckBoxSize} from '../../../molecules/CheckBox/CheckBoxSize.enum';
import {BelowDefaultTab} from '../../../organisms/BelowDefaultTab/BelowDefaultTab';
import {Dialog} from '../../../organisms/Dialog';
import {MainTab} from '../../../organisms/MainTab/MainTab';
import {SubTab} from '../../../organisms/SubTab/SubTab';
import {SubtabItemActiveColorScheme} from '../../../organisms/SubTabItem/constants';
import {StaticLandingGenreProps} from './StaticLandingGenreProps.interface';

export function StaticLandingGenre({
  isMobile,
  isWebView,
  isSubtab,
  title,
  totalCount,
  sortTypeList,
  currentSortType,
  subcategoryList,
  currentSubcategoryUid,
  isShowSortTypeDialog,
  className,
  onClickSortTypeTabItem,
  onClickSubcategoryItem,
  showSortTypeDialog,
  hideSortTypeDialog,
  displayAd,
  promotionBanner,
  isComplete,
  onClickCompletedFilter,
  children,
}: StaticLandingGenreProps) {
  const mainTabList = useMemo(
    () =>
      subcategoryList?.map(({name, param, href}) => ({
        id: param,
        text: name,
        href,
      })) || [],
    [subcategoryList]
  );
  const currentSubcategoryName = useMemo(
    () =>
      subcategoryList?.find(item => item.param === currentSubcategoryUid)?.name,
    [currentSubcategoryUid, subcategoryList]
  );
  const currentSortTypeName = useMemo(
    () => sortTypeList?.find(item => item.param === currentSortType)?.name,
    [currentSortType, sortTypeList]
  );
  const hasSortType = sortTypeList && sortTypeList.length > 0;
  const isCompleteLog = isComplete ? 'y' : 'n';

  return (
    <div
      className={twMerge('flex flex-col w-full grow', className)}
      {...putTiaraObjectProps({
        customProps: {
          page_genre_filter: currentSubcategoryName,
          page_sort: currentSortTypeName,
          completed: isCompleteLog,
        },
        click: {
          layer1: '섹션',
        },
      })}
      {...(isSubtab && {
        [PageViewCustomPropsDataKey.TabType]: 'tab_type=landing_single',
      })}
    >
      {subcategoryList && currentSubcategoryUid && (
        <>
          {(isMobile || isSubtab) && (
            <TabPositionSticky
              depth={isSubtab ? 3 : 1}
              isMobile={isMobile}
              isWebView={isWebView}
              disabled={!isMobile}
            >
              <SubTab
                tabItems={subcategoryList}
                currentTabItemParam={currentSubcategoryUid}
                activeColorScheme={SubtabItemActiveColorScheme['2nd']}
                onClickTab={(clickedTabItemId, e) => {
                  aggregateAndSendLog({
                    targetElem: e?.target as HTMLElement,
                    actionName: '장르_클릭',
                    actionType: 'Event',
                    click: {
                      copy: subcategoryList.find(
                        subcategory => subcategory.param === clickedTabItemId
                      )?.name,
                    },
                    customProps: {
                      user_action: 'click',
                    },
                  });
                  onClickSubcategoryItem &&
                    onClickSubcategoryItem(clickedTabItemId as string);
                }}
                logTabType={LogTabType.Genre}
                {...putTiaraObjectProps({
                  click: {
                    layer1: isSubtab ? '서브탭2' : '장르',
                  },
                })}
              />
            </TabPositionSticky>
          )}
          {!isMobile && !isSubtab && (
            <MainTab
              title={title}
              isMobile={isMobile}
              tabItems={mainTabList}
              currentTabItemId={currentSubcategoryUid}
              useCustomClickEvent={true}
              onClickTab={(clickedTabItemId, e) => {
                aggregateAndSendLog({
                  targetElem: e?.target as HTMLElement,
                  actionName: '장르_클릭',
                  actionType: 'Event',
                  click: {
                    copy: subcategoryList.find(
                      subcategory => subcategory.param === clickedTabItemId
                    )?.name,
                  },
                  customProps: {
                    user_action: 'click',
                  },
                });

                onClickSubcategoryItem &&
                  onClickSubcategoryItem(clickedTabItemId as string);
              }}
              className="px-15pxr"
              logTabType={LogTabType.Genre}
              {...putTiaraObjectProps({
                click: {
                  layer1: isSubtab ? '서브탭2' : '장르',
                },
              })}
            />
          )}
        </>
      )}
      <TabPositionSticky
        depth={isSubtab ? 4 : 2}
        isMobile={isMobile}
        isWebView={isWebView}
        disabled={!isMobile}
      >
        <BelowDefaultTab className="px-15pxr">
          {(!!totalCount || totalCount === 0) && (
            <BelowDefaultTab.TextLabel>
              {formatWithCommas(totalCount)}개
            </BelowDefaultTab.TextLabel>
          )}
          <BelowDefaultTab.CheckBox
            isChecked={isComplete}
            size={CheckBoxSize.SubTab}
            onClick={e =>
              LOG_CLICK_EVENT('완결작보기_클릭', e, () =>
                onClickCompletedFilter()
              )
            }
            {...putTiaraObjectProps({
              click: {
                layer1: '리스팅옵션',
                copy: isComplete ? 'n' : 'y',
              },
            })}
            {...{
              [PageViewCustomPropsDataKey.Completed]: `completed=${isCompleteLog}`,
            }}
          >
            {t('landing_filter_completed')}
          </BelowDefaultTab.CheckBox>
          {hasSortType && currentSortTypeName && (
            <BelowDefaultTab.SelectBox
              onClick={showSortTypeDialog}
              {...{
                [PageViewCustomPropsDataKey.PageSort]: `page_sort=${currentSortTypeName}`,
              }}
              iconType={IconType.TabSorting}
            >
              {currentSortTypeName}
            </BelowDefaultTab.SelectBox>
          )}
        </BelowDefaultTab>
      </TabPositionSticky>

      {hasSortType && (
        <Dialog
          isOpen={!!isShowSortTypeDialog}
          onClose={hideSortTypeDialog}
          isMobile={isMobile}
          {...putTiaraObjectProps({
            customProps: {
              page_genre_filter: currentSubcategoryName,
              page_sort: currentSortTypeName,
              completed: isCompleteLog,
            },
          })}
        >
          <Dialog.Dimmed />
          <Dialog.List className="space-y-0">
            {sortTypeList.map(sortType => (
              <Dialog.Option
                key={sortType.id}
                onClick={e => {
                  LOG_CLICK_EVENT('정렬_클릭', e, () =>
                    onClickSortTypeTabItem(sortType.param)
                  );
                }}
                isChecked={sortType.param === currentSortType}
                {...putTiaraObjectProps({
                  click: {
                    layer1: '리스팅옵션',
                    copy: sortType.name,
                  },
                })}
              >
                {sortType.name}
              </Dialog.Option>
            ))}
          </Dialog.List>
          <Dialog.BottomCombination>
            <Dialog.Button onClick={hideSortTypeDialog}>
              {t('common_cancel_button')}
            </Dialog.Button>
          </Dialog.BottomCombination>
        </Dialog>
      )}
      {isSubtab && promotionBanner && (
        <PromotionBannerContainer promotionBanner={promotionBanner} />
      )}
      {displayAd && isMobile && (
        <DisplayAdContainer
          displayAd={displayAd}
          className="mx-11pxr mb-10pxr"
        />
      )}
      {children}
    </div>
  );
}
